import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back!');
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <div className="bg-white p-10 rounded-3xl shadow-2xl w-full max-w-md border border-zinc-100">
        <div className="text-center mb-10">
          <div className="flex justify-center mb-8">
            <img
              src="/gympe.svg"
              alt="Gympe"
              className="h-12 w-auto"
            />
          </div>
          <h2 className="text-3xl font-black text-zinc-900 tracking-tight">Welcome Back</h2>
          <p className="text-zinc-500 mt-2 font-medium">Log in to manage your fitness empire</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-xs font-black uppercase tracking-widest text-zinc-400 mb-2 ml-1">
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium"
              placeholder="name@gym.com"
              disabled={loading}
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2 ml-1">
              <label htmlFor="password" className="block text-xs font-black uppercase tracking-widest text-zinc-400">
                Password
              </label>
              <Link to="/auth/forgot-password" className="text-xs font-bold text-zinc-950 hover:text-[#AAD816] transition-colors">
                Forgot?
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#AAD816] focus:border-transparent transition-all duration-300 font-medium"
              placeholder="••••••••"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-zinc-950 hover:bg-[#AAD816] text-white hover:text-zinc-950 font-black py-4 px-6 rounded-2xl transition-all duration-300 shadow-xl shadow-zinc-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                Verifying...
              </div>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Sign In
                <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            )}
          </button>

          <div className="text-center mt-8">
            <span className="text-sm font-medium text-zinc-500">
              New to Gympe?{' '}
              <Link to="/register" className="font-bold text-zinc-950 hover:text-[#AAD816] transition-colors underline-offset-4 hover:underline">
                Create an account
              </Link>
            </span>
          </div>
        </form>
      </div>
    </div>
  );
} 